import { getCurrentCulture } from 'owa-localize';

export function createNameCollator(): Intl.Collator {
    return new Intl.Collator(getCurrentCulture() || undefined, {
        sensitivity: 'base',
        numeric: true,
    });
}

/**
 * Sorts display names naturally, keeping missing names last and equal-name rows in service order.
 * Exact-name tiebreaks keep the names used by group headers contiguous.
 */
export default function sortRowsByName<T>(
    rows: readonly T[],
    getName: (row: T) => string | undefined,
    descending: boolean = false
): T[] {
    const locale = getCurrentCulture() || undefined;
    const collator = createNameCollator();
    const variantCollator = new Intl.Collator(locale, { sensitivity: 'variant', numeric: true });
    const direction = descending ? -1 : 1;

    return rows
        .map(row => {
            const rawName = getName(row) ?? '';
            return { row, rawName, name: rawName.trim() };
        })
        .sort((a, b) => {
            if (!a.name || !b.name) {
                return (a.name ? 0 : 1) - (b.name ? 0 : 1);
            }

            const comparison =
                collator.compare(a.name, b.name) ||
                variantCollator.compare(a.name, b.name) ||
                (a.rawName < b.rawName ? -1 : a.rawName > b.rawName ? 1 : 0);

            return direction * comparison;
        })
        .map(({ row }) => row);
}
